/**
 * Shared filing-review field constants/helpers used by BOTH the batch review panel
 * (BatchFilingReview) and the single-policy review screen (FilingReview), so a domain change is made
 * in one place instead of drifting across the two screens.
 */

export interface DropdownOption {
    label: string;
    value: string;
}

/** Minimal shape of a review field this module needs (both screens' FilingField satisfy it). */
export interface OptionField {
    key: string;
    value: string | number | null;
}

/** FSLSO TaxStatus (0-5) — labels mirror flTaxStatus.js (TAX_STATUS_LABELS) on the internal-api so the
 *  reviewer's override dropdown matches exactly what the filing sends. Only rendered when the backend
 *  sends an editable `taxStatus` field. */
export const TAX_STATUS_OPTIONS: DropdownOption[] = [
    { value: '0', label: '0 — Taxable' },
    { value: '1', label: '1 — Non-taxable · aircraft (fee + assessments)' },
    { value: '2', label: '2 — Non-taxable · governmental (assessments only)' },
    { value: '3', label: '3 — Non-taxable · cargo / marine (fee + assessments)' },
    { value: '4', label: '4 — Non-taxable · EMPA surcharge only' },
    { value: '5', label: '5 — Non-taxable · fully exempt' },
];

/** UMR is a Lloyd's-only concept — this is the plain-language label shown in place of the terse backend
 *  "UMR (Lloyd's, v2.4)". */
export const LLOYDS_UMR_LABEL = "Lloyd's Unique Market Reference";

/** The Lloyd's-of-London alien NAIC — the one carrier for which a UMR applies. */
export const LLOYDS_NAIC = 'AA1122000';

/** True when the row's writing insurer is Lloyd's of London (the only case where a UMR applies).
 *  Detected from the manual-review Lloyd's flag, the Lloyd's NAIC, or a "Lloyd" insurer name. */
export function isLloydsInsurer(fields: OptionField[], hasLloydsFlag: boolean): boolean {
    if (hasLloydsFlag) return true;
    const naic = fields.find((f) => /insurernaic/i.test(f.key))?.value;
    if (String(naic || '').toUpperCase() === LLOYDS_NAIC) return true;
    const name = fields.find((f) => /(writinginsurername|insurername)/i.test(f.key))?.value;
    return /lloyd/i.test(String(name || ''));
}

// ── Structured address fields ─────────────────────────────────────────────────────────────────────
// The backend review Fields include per-component address fields (editable, required:false). Both
// screens render them via the grouped AddressFieldsEditor and hide the legacy combined line.
export const MAILING_ADDRESS_KEYS = { line1: 'addressLine1', line2: 'addressLine2', city: 'city', state: 'state', zip: 'zip' };
export const RISK_ADDRESS_KEYS = { line1: 'riskAddressLine1', line2: 'riskAddressLine2', city: 'riskCity', state: 'riskState', zip: 'riskZip' };
export const ADDRESS_COMPONENT_KEYS = new Set<string>([...Object.values(MAILING_ADDRESS_KEYS), ...Object.values(RISK_ADDRESS_KEYS)]);
/** Legacy combined address fields — kept by the backend for back-compat but no longer the edit surface. */
export const LEGACY_ADDRESS_KEYS = new Set<string>(['mailingAddress', 'physicalRiskAddress']);

/** Build an AddressFieldsEditor component from a review field, or undefined when the field is absent. */
export function addressComponent(fields: OptionField[], key: string): { key: string; value: string } | undefined {
    const f = fields.find((x) => x.key === key);
    if (!f) return undefined;
    return { key, value: f.value != null ? String(f.value) : '' };
}

/** True when the review sends any of the given address-component fields (i.e. render the editor). */
export function hasAddressComponents(fields: OptionField[], keys: Record<string, string>): boolean {
    return Object.values(keys).some((k) => fields.some((f) => f.key === k));
}
